// GHOST-2B — the app's own logo lockup.
//
// WHAT CHANGED AND WHY. This rendered a square icon with the brand name set
// as TYPE underneath it, in a colour pair hardcoded here. GHOST-2 installed
// the real wordmark and wired nothing to it, so the live dashboard showed a
// ghost tile beside the words of the old name, which contradicts itself.
//
// The wordmark is inlined as a React component rather than an img, because
// its letters are fill="currentColor" and an img cannot inherit that. One
// file therefore serves the light sidebar and the dark one without markup
// hardcoding a theme assumption. See src/components/brand/GhostWordmark.tsx,
// which is generated from public/brand/wordmark.svg.
//
// THE BRAND TEXT IS GONE, not hidden and not kept as a fallback. The wordmark
// carries the name now, and two sources for one piece of copy is exactly how
// a rename leaves leftovers behind.

import { cn } from '../../lib/utils'
import { GhostWordmark } from '../brand/GhostWordmark'

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** False renders the square icon alone. Meaning unchanged. */
  showWordmark?: boolean
  className?: string
}

/**
 * MINIMUM LEGIBLE WIDTH FOR THE LOCKUP, in CSS pixels.
 *
 * HOW THIS WAS JUDGED, not picked. The lockup's viewBox is 3727.98 by 1133,
 * so it is 3.29 wide to tall. CRM's cap height is about 11 percent of the
 * total height, so at a rendered width W its caps measure
 *   0.11 * (W / 3.29) = 0.0334 * W
 * Uppercase letterforms stop resolving below roughly 5 CSS pixels of cap
 * height, where the stems fall under one device pixel at 1x. Solving
 * 0.0334 * W >= 5 gives W >= 150.
 *
 * 150 is therefore the floor, and any size below it renders the square icon
 * instead. The smallest size ships at 160, which puts CRM's caps at 5.3
 * pixels: above the floor with a little room rather than exactly on it.
 */
export const WORDMARK_MIN_WIDTH = 150

const SIZES = {
  sm: { icon: 'h-8 w-8', lockupWidth: 160 },
  md: { icon: 'h-10 w-10', lockupWidth: 190 },
  lg: { icon: 'h-14 w-14', lockupWidth: 240 },
  xl: { icon: 'h-20 w-20', lockupWidth: 300 },
}

/**
 * Whether an instance can show the lockup or must fall back to the icon.
 * Exported so the rule is unit tested directly rather than inferred from a
 * rendered tree.
 */
export function shouldRenderLockup(widthPx: number, showWordmark: boolean): boolean {
  return showWordmark && widthPx >= WORDMARK_MIN_WIDTH
}

export default function Logo({ size = 'md', showWordmark = true, className }: LogoProps) {
  const sizing = SIZES[size]

  if (!shouldRenderLockup(sizing.lockupWidth, showWordmark)) {
    return (
      <img src="/brand/app-icon.svg" alt="" className={cn(sizing.icon, 'shrink-0', className)} />
    )
  }

  // One horizontal lockup. No separate icon and no separate text: the
  // wordmark already contains the ghost as its letter o.
  return (
    <GhostWordmark
      className={cn('shrink-0 text-foreground', className)}
      style={{ width: sizing.lockupWidth }}
    />
  )
}
